import { useState } from 'react';
import MobileTermometro from '../MobileTermometro';
import MobileCraqueBagre from '../MobileCraqueBagre';
import MobileBolao from '../MobileBolao';
import { isIntervaloAtivo } from '../../../domain/usecases/partida';
import { JogoPlacarHeader } from './JogoPlacarHeader';
import {
  EstatisticasCard,
  IncidentesTimeline,
  AiPreviewCard,
  EscalacaoCard,
  FunFactsCard,
} from './JogoSharedCards';
import type { FutebolPartidaAtiva, FutebolJogoDia } from '@/shared/types';

type Tab = 'ao_vivo' | 'bolao' | 'detalhes';

interface TabDef { id: Tab; label: string; dot?: boolean }

function TabBar({ tabs, active, onChange }: { tabs: TabDef[]; active: Tab; onChange: (t: Tab) => void }) {
  return (
    <div className="flex border-b border-white/[0.05]">
      {tabs.map(tab => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`relative flex min-h-11 flex-1 items-center justify-center gap-1.5 transition-colors ${
            active === tab.id ? 'text-white' : 'text-white/40 active:text-white/70'
          }`}
        >
          <span className="text-[12px] font-semibold">{tab.label}</span>
          {tab.dot && <span className="h-1.5 w-1.5 shrink-0 animate-pulse rounded-full bg-orange-400" />}
          {active === tab.id && (
            <span className="absolute bottom-0 left-6 right-6 h-0.5 rounded-full bg-orange-500" />
          )}
        </button>
      ))}
    </div>
  );
}

interface Props {
  telaId: string;
  partida: FutebolPartidaAtiva;
  jogo: FutebolJogoDia | null;
  exibirBolao: boolean;
}

export function JogoAoVivoTabs({ telaId, partida, jogo, exibirBolao }: Props) {
  const [activeTab, setActiveTab] = useState<Tab>('ao_vivo');

  const isHT        = isIntervaloAtivo(partida.ultimo_evento ?? null);
  const isEncerrado = partida.tempo_jogo === 'FT';
  const nomeCasa    = jogo?.time_casa      ?? '–';
  const nomeVis     = jogo?.time_visitante ?? '–';

  const tabs: TabDef[] = [
    { id: 'ao_vivo',  label: 'No ar',  dot: isHT },
    ...(exibirBolao ? [{ id: 'bolao' as Tab, label: 'Bolão' }] : []),
    { id: 'detalhes', label: 'Detalhes' },
  ];

  return (
    <>
      <div className="sticky top-0 z-20 shrink-0 bg-jukebox-bg">
        <JogoPlacarHeader partida={partida} jogo={jogo} modo="ao_vivo" />
        <TabBar tabs={tabs} active={activeTab} onChange={setActiveTab} />
      </div>

      <div className="flex-1 space-y-5 px-4 pb-24 pt-5">
        {activeTab === 'ao_vivo' && (
          <>
            {isHT && (
              <MobileCraqueBagre
                telaId={telaId}
                fixtureId={partida.fixture_id}
                ultimoEvento={partida.ultimo_evento ?? null}
              />
            )}

            {!isEncerrado && partida.fixture_id != null && (
              <MobileTermometro
                telaId={telaId}
                fixtureId={partida.fixture_id}
                nomeCasa={jogo?.time_casa ?? undefined}
                nomeVisitante={jogo?.time_visitante ?? undefined}
                escudoCasa={jogo?.escudo_casa ?? undefined}
                escudoVisitante={jogo?.escudo_visitante ?? undefined}
              />
            )}

            {partida.incidents && partida.incidents.length > 0 && (
              <IncidentesTimeline
                incidents={partida.incidents}
                nomeCasa={nomeCasa}
                nomeVisitante={nomeVis}
              />
            )}

            {isEncerrado && (!partida.incidents || partida.incidents.length === 0) && (
              <p className="text-center text-white/50 text-sm py-12">Jogo encerrado</p>
            )}
          </>
        )}

        {exibirBolao && partida.fixture_id != null && (
          <div className={activeTab === 'bolao' ? '' : 'hidden'}>
            <MobileBolao
              telaId={telaId}
              fixtureId={partida.fixture_id}
              tempoJogo={partida.tempo_jogo}
              nomeCasa={jogo?.time_casa ?? undefined}
              nomeVisitante={jogo?.time_visitante ?? undefined}
              escudoCasa={jogo?.escudo_casa ?? undefined}
              escudoVisitante={jogo?.escudo_visitante ?? undefined}
            />
          </div>
        )}

        {activeTab === 'detalhes' && (
          <>
            {partida.stats && (
              <EstatisticasCard stats={partida.stats} nomeCasa={nomeCasa} nomeVisitante={nomeVis} />
            )}
            {partida.lineup && partida.lineup.status !== 'unavailable' && (
              <EscalacaoCard lineup={partida.lineup} nomeCasa={nomeCasa} nomeVisitante={nomeVis} />
            )}
            {partida.ai_preview && <AiPreviewCard text={partida.ai_preview} />}
            {partida.funfacts && partida.funfacts.length > 0 && (
              <FunFactsCard funfacts={partida.funfacts} />
            )}
            {!partida.stats && !partida.ai_preview &&
              (!partida.lineup || partida.lineup.status === 'unavailable') &&
              (!partida.funfacts || partida.funfacts.length === 0) && (
                <p className="text-center text-white/50 text-sm py-12">Detalhes indisponíveis</p>
            )}
          </>
        )}
      </div>
    </>
  );
}
